import { useEffect, useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { MaintenanceModal } from "@/components/MaintenanceModal";
import { ReportProblemModal } from "@/components/ReportProblemModal";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EventChip } from "@/components/EventChip";
import { ChevronLeft, ChevronRight, Loader2, FileSpreadsheet, Plus } from "lucide-react";

const WEEKDAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

export default function CalendarPage() {
  const { user } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [maintenances, setMaintenances] = useState([]);
  const [availableMap, setAvailableMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ park_id: "", date: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [parks, setParks] = useState([]);
  const isAdmin = user?.role === "admin";

  const addMaintenance = async () => {
    if (!addForm.park_id) return toast.error("Sélectionnez un parc.");
    if (!addForm.date) return toast.error("Sélectionnez une date.");
    setAddSaving(true);
    try {
      await api.post("/maintenances", addForm);
      toast.success("Entretien ajouté et assigné à vous-même.");
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/maintenances/${deleteTarget.id}`);
      toast.success("Entretien supprimé.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportMaintenances = async () => {
    setExporting(true);
    try {
      await downloadFile("/maintenances/export", "entretiens_completes.xlsx");
      toast.success("Liste des entretiens générée.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const params = { start: format(gridStart, "yyyy-MM-dd"), end: format(gridEnd, "yyyy-MM-dd") };
      const [mRes, aRes] = await Promise.all([
        api.get("/maintenances", { params }),
        api.get("/availability/available", { params }),
      ]);
      setMaintenances(mRes.data);
      setAvailableMap(aRes.data || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  useEffect(() => {
    if (addOpen && parks.length === 0) {
      api.get("/parks").then(({ data }) => setParks(data.filter((p) => p.active))).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [addOpen]);

  const days = useMemo(() => {
    const arr = [];
    let d = gridStart;
    while (d <= gridEnd) { arr.push(d); d = addDays(d, 1); }
    return arr;
  }, [gridStart, gridEnd]);

  const byDate = useMemo(() => {
    const map = {};
    for (const m of maintenances) {
      (map[m.date] = map[m.date] || []).push(m);
    }
    return map;
  }, [maintenances]);

  const onEventClick = (m) => {
    if (!m.assigned_to && m.status !== "completed") { setAssignTarget(m); return; }
    if (m.assigned_to === user.id) { setSelected(m); setDetailOpen(true); return; }
    if (isAdmin) { setSelected(m); setDetailOpen(true); return; }
    toast.info(`Assigné à ${m.assigned_to_name}`);
  };

  const confirmAssign = async () => {
    try {
      await api.post(`/maintenances/${assignTarget.id}/assign`);
      toast.success("Entretien assigné à vous-même.");
      setAssignTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const eventClasses = (m) => {
    if (m.status === "completed")
      return "bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-500/50 hover:bg-blue-500/30";
    if (!m.assigned_to)
      return "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 hover:bg-red-500/25";
    if (m.assigned_to === user.id)
      return "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/50 hover:bg-emerald-500/30";
    return "bg-muted text-muted-foreground border border-border";
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Calendrier des entretiens</h1>
          <p className="text-muted-foreground mt-1">
            N'oubliez pas de terminer l'entretien <span className="font-medium text-[#990000] dark:text-[#f5b727]">avant de quitter le parc.</span>
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:items-center">
          <Button data-testid="add-maintenance-btn" onClick={() => { setAddForm({ park_id: "", date: format(new Date(), "yyyy-MM-dd") }); setAddOpen(true); }} className="w-full sm:w-auto active:scale-95 transition-transform sm:mr-1">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un entretien
          </Button>
          <div className="flex items-center justify-center gap-2">
            <Button data-testid="prev-month-btn" variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[150px] text-center font-heading text-lg font-semibold capitalize">
              {format(cursor, "MMMM yyyy", { locale: fr })}
            </div>
            <Button data-testid="next-month-btn" variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {d}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-32 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const events = byDate[key] || [];
              const inMonth = isSameMonth(day, cursor);
              const today = isSameDay(day, new Date());
              return (
                <div
                  key={key}
                  className={`min-h-[110px] border-b border-r border-border p-1.5 ${inMonth ? "" : "bg-muted/20"}`}
                >
                  <div className={`text-xs font-medium mb-1 flex items-center justify-center h-6 w-6 rounded-full ${today ? "bg-primary text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground/50"}`}>
                    {format(day, "d")}
                  </div>
                  <div className="space-y-1">
                    {events.map((m) => (
                      <EventChip
                        key={m.id}
                        m={m}
                        isAdmin={isAdmin}
                        availableNames={availableMap[m.date] || []}
                        colorClass={eventClasses(m)}
                        onOpen={onEventClick}
                        onDelete={setDeleteTarget}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/60 border border-emerald-500" /> Mes entretiens</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/40 border border-red-500" /> Non-assigné</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-muted border border-border" /> Assigné à un autre</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500/50 border border-blue-500" /> Terminé</span>
        </div>
        <Button data-testid="export-maintenances-btn" onClick={exportMaintenances} disabled={exporting} variant="secondary" className="active:scale-95 transition-transform">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          Liste des entretiens
        </Button>
      </div>

      <MaintenanceModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        maintenance={selected}
        isAdmin={isAdmin}
        onChanged={load}
        onReport={(m) => { setReportTarget(m); setReportOpen(true); }}
      />

      <ReportProblemModal open={reportOpen} onOpenChange={setReportOpen} maintenance={reportTarget} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-maintenance-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter un entretien</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            L'entretien sera automatiquement assigné à vous-même.
          </p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Parc</Label>
              <Select value={addForm.park_id} onValueChange={(v) => setAddForm({ ...addForm, park_id: v })}>
                <SelectTrigger data-testid="add-maintenance-park-select"><SelectValue placeholder="Choisir un parc" /></SelectTrigger>
                <SelectContent>
                  {parks.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input data-testid="add-maintenance-date-input" type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button data-testid="add-maintenance-save-btn" onClick={addMaintenance} disabled={addSaving}>
              {addSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <AlertDialogContent data-testid="assign-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>S'assigner cet entretien ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de vous assigner l'entretien de <strong>{assignTarget?.park_name}</strong> prévu le {assignTarget?.date}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="assign-confirm-btn" onClick={confirmAssign}>Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-event-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'entretien</AlertDialogTitle>
            <AlertDialogDescription>
              Souhaitez vous vraiment supprimer l'entretien du {deleteTarget?.date} ({deleteTarget?.park_name}) ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-event-confirm-btn" onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
